import { RestClient } from './RestClient'
import store from 'src/store'

export class InvoiceClient extends RestClient {
  /**
   * InvoiceClient constructor
   *
   * @constructor
   */
  constructor () {
    super()

    let currentTeam = store.getters.activeTeam
    this.baseUrl = `/teams/${currentTeam.id}/invoices`
  }

  /**
   * Get a list of invoices
   *
   * @returns {AxiosPromise}
   */
  list () {
    return this.call('get', this.baseUrl)
  }

  /**
   * Show the specified invoices
   *
   * @param {(...number|number[])} id - Invoices to show
   *
   * @returns {AxiosPromise}
   */
  get (...id) {
    return this.call('get', `${this.baseUrl}/${this.parseAndJoin(id)}`)
  }

  /**
   * Get a PDF invoice
   *
   * @param id
   *
   * @returns {AxiosPromise}
   */
  pdf (id, applyPdfTemplate = true) {
    this.addParam('apply_pdf_template', applyPdfTemplate)

    return this.call('get', `${this.baseUrl}/${id}`, null, {
      'Accept': 'application/pdf'
    }, {
      responseType: 'arraybuffer'
    })
  }

  /**
   * Get a PDF invoice preview
   *
   * @param data
   *
   * @returns {AxiosPromise}
   */
  pdfPreview (data) {
    return this.call('post', `${this.baseUrl}/preview`, data, {
      'Accept': 'application/pdf'
    }, {
      responseType: 'arraybuffer'
    })
  }

  /**
   * Create a new invoice
   *
   * @param {Object} data - Invoice data
   *
   * @returns {AxiosPromise}
   */
  create (data) {
    return this.call('post', this.baseUrl, data)
  }

  /**
   * Update an existing invoice
   *
   * @param {number} invoiceId - Invoice id
   * @param {Object} data - Invoice data
   *
   * @returns {AxiosPromise}
   */
  update (invoiceId, data) {
    return this.call('patch', `${this.baseUrl}/${invoiceId}`, data)
  }

  /**
   * Delete the specified invoices
   *
   * @param {(...number|number[])} invoiceId - Invoices which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroy (...invoiceId) {
    return this.call('delete', `${this.baseUrl}/${this.parseAndJoin(invoiceId)}`)
  }

  /**
   * Add a payment to an invoice
   *
   * @param {number} invoiceId - Invoice id
   * @param {Object} paymentData - Payment data
   *
   * @returns {AxiosPromise}
   */
  addPayment (invoiceId, paymentData) {
    return this.call('post', `${this.baseUrl}/${invoiceId}/payments`, paymentData)
  }

  /**
   * Update an existing payment
   *
   * @param {number} invoiceId - Invoice id
   * @param {number} paymentId - Payment id
   * @param {Object} paymentData - Payment data
   *
   * @returns {AxiosPromise}
   */
  updatePayment (invoiceId, paymentId, paymentData) {
    return this.call('patch', `${this.baseUrl}/${invoiceId}/payments/${paymentId}`, paymentData)
  }

  /**
   * Delete the specified payments
   *
   * @param {number} invoiceId - Invoice id
   * @param {numer} paymentId - Payment which should be deleted
   *
   * @returns {AxiosPromise}
   */
  destroyPayment (invoiceId, paymentId) {
    return this.call('delete', `${this.baseUrl}/${invoiceId}/payments/${paymentId}`)
  }

  /**
   * Set the status of an invoice
   *
   * @param {number} invoiceId - Invoice id
   * @param {Object} statusData - Status data
   *
   * @returns {AxiosPromise}
   */
  setStatus (invoiceId, statusData) {
    return this.call('post', `${this.baseUrl}/${invoiceId}/statuses`, statusData)
  }

  /**
   * Delete the specified statuses
   *
   * @param {number} invoiceId - Invoice id
   * @param {(...number|number[])} statusId - Statuses which should be deleted
   *
   * @returns {AxiosPromise}
   */
  deleteStatus (invoiceId, ...statusId) {
    return this.call('delete', `${this.baseUrl}/${invoiceId}/statuses/${this.parseAndJoin(statusId)}`)
  }

  /**
   * Add a note to an invoice
   *
   * @param invoiceId
   * @param noteData
   * @returns {AxiosPromise}
   */
  addNote (invoiceId, noteData) {
    return this.call('post', `${this.baseUrl}/${invoiceId}/notes`, noteData)
  }

  /**
   * Search for invoices
   *
   * @param query
   * @returns {AxiosPromise}
   */
  search (query) {
    this.params.q = query

    return this.call('get', `${this.baseUrl}/search`)
  }

  /**
   * Start an invoice export process
   *
   * @param {Object} filters - Export filters
   *
   * @returns {AxiosPromise}
   */
  export (filters = {}) {
    return this.call('post', `${this.baseUrl}/export`, filters)
  }

  /**
   * Send an invoice as email
   *
   * @param {number} invoiceId - Invoice id
   * @param {Object} data - E-Mail data
   * @returns {AxiosPromise}
   */
  send (invoiceId, data) {
    return this.call('post', `${this.baseUrl}/${invoiceId}/send`, data)
  }

  /**
   * Commit the invoice
   *
   * @param {number} invoiceId - Invoice id
   * @returns {AxiosPromise}
   */
  commit (invoiceId) {
    return this.call('post', `${this.baseUrl}/${invoiceId}/commit`)
  }
}
